import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import testimonialsData from "../data/testimonial.json";

const TestimonialItem = ({ data }) => (
  <div className="flex flex-col gap-6 py-10 border-b border-white/5 last:border-0 group">
    {/* Feedback text */}
    <p className="text-xl md:text-3xl text-slate-300 font-serif italic leading-relaxed tracking-wide transition-colors duration-500 group-hover:text-white">
      "{data.feedback}"
    </p>

    {/* User Info & Badges */}
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
      <div className="flex items-center gap-4">
        <img
          src={`/assets/testimonial/${data.avatar}`}
          alt={data.name}
          className="w-12 h-12 rounded-full object-cover opacity-70 group-hover:opacity-100 transition-all duration-500"
        />
        <div>
          <p className="text-base font-bold text-slate-200">{data.name}</p>
          <p className="text-sm text-slate-400 font-medium tracking-wide">
            {data.role}{" "}
            <span className="text-purple-400/70">@ {data.company}</span>
          </p>
        </div>
      </div>

      {/* Badge & LinkedIn */}
      <div className="flex items-center gap-4">
        <span className="text-xs font-semibold text-emerald-400/80 tracking-widest uppercase bg-emerald-400/10 px-3 py-1 rounded-full border border-emerald-400/20">
          {data.badge}
        </span>
        <a
          href={data.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-500 hover:text-[#0A66C2] transition-colors p-2 hover:bg-white/5 rounded-full"
          aria-label="LinkedIn Profile"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
          </svg>
        </a>
      </div>
    </div>
  </div>
);

const InfiniteTestimonialScroll = () => {
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const yOffset = useRef(0);
  const direction = useRef(-1); // 1 for down, -1 for up
  const speed = 5; // Subtle, elegant speed

  useEffect(() => {
    let lastScrollY = window.scrollY;

    // Dynamic Direction logic based on page scroll
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        direction.current = -1; // Page moving down -> Track moves down
      } else if (currentScrollY < lastScrollY) {
        direction.current = 1; // Page moving up -> Track moves up
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let animationId;

    const renderLoop = () => {
      if (!isPaused && containerRef.current) {
        const container = containerRef.current;
        // Since we render the array 3 times, exactly 1/3 of the scrollHeight is one full loop
        const singleSetHeight = container.scrollHeight / 3;

        yOffset.current += speed * direction.current;

        // Seamless wrapping logic
        if (yOffset.current > 0) {
          yOffset.current -= singleSetHeight;
        } else if (yOffset.current <= -singleSetHeight) {
          yOffset.current += singleSetHeight;
        }

        container.style.transform = `translateY(${yOffset.current}px)`;
      }
      animationId = requestAnimationFrame(renderLoop);
    };

    animationId = requestAnimationFrame(renderLoop);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);

  return (
    <div
      className="relative w-full h-[80vh] overflow-hidden flex flex-col justify-center"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      style={{
        maskImage:
          "linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)",
      }}
    >
      <div
        ref={containerRef}
        className="flex flex-col gap-4 will-change-transform"
      >
        {/* Render 3 identical sets to ensure infinite seamless scrolling */}
        {[...testimonialsData, ...testimonialsData, ...testimonialsData].map(
          (item, i) => (
            <TestimonialItem key={`testim-${i}`} data={item} />
          ),
        )}
      </div>
    </div>
  );
};

const Testimonials = () => {
  return (
    <section className="relative min-h-screen text-slate-200 py-24 border-t border-white/5 overflow-hidden flex items-center">
      {/* Cinematic Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[40vw] h-[40vw] bg-purple-600/10 rounded-full blur-[120px] opacity-60 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
        <div className="flex flex-col-reverse lg:flex-row items-center justify-between gap-16 lg:gap-24">
          {/* LEFT: Scrolling Testimonials (55-60%) */}
          <div className="w-full lg:w-[55%] relative">
            {/* Huge Decorative Quotes */}
            <span className="absolute -top-10 -left-10 text-[180px] font-serif text-white/[0.1] leading-none pointer-events-none select-none z-0">
              ❝
            </span>
            <span className="absolute -bottom-20 -right-10 text-[180px] font-serif text-white/[0.1] leading-none pointer-events-none select-none z-0">
              ❞
            </span>

            <div className="relative z-10">
              <InfiniteTestimonialScroll />
            </div>
          </div>

          {/* RIGHT: Static Content (40-45%) */}
          <div className="w-full lg:w-[40%] flex flex-col items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-emerald-500"></div>
                <span className="text-emerald-400 font-medium tracking-widest uppercase text-sm">
                  Trust
                </span>
              </div>

              <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-8 leading-tight tracking-tight">
                Real Words. <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                  Real Impact.
                </span>
              </h2>

              <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-md mb-10">
                A collection of experiences, recommendations, and trust earned
                through genuine collaboration and high-quality product
                execution.
              </p>

              <div className="flex gap-12 border-t border-white/10 pt-8 mt-4 w-full">
                <div>
                  <h4 className="text-4xl font-light text-white mb-2">
                    15<span className="text-purple-400">+</span>
                  </h4>
                  <p className="text-sm tracking-wide text-slate-500 uppercase font-semibold">
                    Happy Clients
                  </p>
                </div>
                <div>
                  <h4 className="text-4xl font-light text-white mb-2">
                    100<span className="text-blue-400">%</span>
                  </h4>
                  <p className="text-sm tracking-wide text-slate-500 uppercase font-semibold">
                    Success Rate
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
